'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getHostName, PaymentStylesMap } from '~/utils/utils';
import {
  IPaymentConnectorPostMessage,
  IPaymentFrameElementProps,
} from '~/interfaces/payment';
import { submitPayment } from '~/utils/payment/submit-visa-order';
import { paymentConnectorSubmitMessage } from '~/utils/payment/post-iframe-message';
import { modifyAdyenContent } from '~/utils/payment/modify-adyen-content';
import { TYPES } from '~/modules/payment/constants';
import { useSubmitPaymentClicked, useSetSyfOverlay } from '~/store/wmap-store';

const PaymentIframe: React.FC<IPaymentFrameElementProps> = ({
  iframeAriaLabel,
  displayContent,
  sourceUrl,
  messageOrigin,
  paymentType,
  cartId,
  isForterValidationEnabled,
  orderDetails,
  usaEPayErrorMessages,
  isForterValidationEnabledForAdyen,
}) => {
  const router = useRouter();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [popupOverrideClass] = useState<string>('');
  const submitPaymentClicked = useSubmitPaymentClicked();
  const setSyfOverlay = useSetSyfOverlay();
  const postMessageToIframe = useCallback(
    (params: IPaymentConnectorPostMessage) => {
      if (iframeRef?.current?.contentWindow?.postMessage) {
        iframeRef.current.contentWindow.postMessage(
          params.message,
          params.targetOrigin,
        );
      }
    },
    [],
  );
  useEffect(() => {
    if (submitPaymentClicked) {
      postMessageToIframe(paymentConnectorSubmitMessage());
    }
  }, [postMessageToIframe, submitPaymentClicked]);
  // Event listener for post messages
  const onEvent = useCallback(
    (event: MessageEvent) => {
      const sourceHost = getHostName(sourceUrl);
      const eventHost = getHostName(event.origin);
      const requestHost = getHostName(window.location.origin);
      const messageHost = getHostName(messageOrigin);

      // Security check: Ensure the origin matches
      if (
        (sourceUrl &&
          !(
            sourceHost === eventHost ||
            requestHost === eventHost ||
            messageHost === eventHost
          )) ||
        (!sourceUrl &&
          !(requestHost === eventHost || messageHost === eventHost))
      ) {
        return;
      }

      submitPayment(
        isForterValidationEnabled,
        event,
        cartId,
        orderDetails,
        router,
        setSyfOverlay,
        undefined,
        usaEPayErrorMessages,
        isForterValidationEnabledForAdyen,
        paymentType === TYPES.ADYEN
          ? (height) => {
              if (iframeRef.current) {
                iframeRef.current.style.height = `${height}px`;
              }
            }
          : undefined,
      );
    },
    [
      cartId,
      isForterValidationEnabled,
      messageOrigin,
      orderDetails,
      sourceUrl,
      router,
      setSyfOverlay,
      usaEPayErrorMessages,
      isForterValidationEnabledForAdyen,
      paymentType,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', onEvent);
    return () => {
      window.removeEventListener('message', onEvent);
    };
  }, [onEvent]);

  // Update iframe styles
  useEffect(() => {
    const updateStyles = (
      paymentType: string,
      iframeRef: React.RefObject<HTMLIFrameElement | null>,
      displayContent: string,
    ) => {
      const styleContent = PaymentStylesMap[paymentType];
      if (styleContent) {
        const innerDocument = iframeRef.current?.contentDocument;
        if (innerDocument) {
          innerDocument.open();
          innerDocument.write(displayContent);
          const styleElement = innerDocument.createElement('style');
          styleElement.textContent = styleContent;
          innerDocument.head.appendChild(styleElement);
          innerDocument.close();
        }
      }
    };

    if (paymentType && displayContent && iframeRef?.current?.contentDocument) {
      const contentToRender =
        paymentType === TYPES.ADYEN
          ? modifyAdyenContent(displayContent)
          : displayContent;

      updateStyles(paymentType, iframeRef, contentToRender);
    }
  }, [displayContent, paymentType]);

  return (
    <iframe
      ref={iframeRef}
      className={popupOverrideClass}
      title={iframeAriaLabel}
      aria-label={iframeAriaLabel}
      src={sourceUrl}
      sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
    />
  );
};

export default PaymentIframe;
